// Guard'ы роутинга (app.md §6–7). Реализация — T-009.
//
// Модель доступа зафиксирована и меняться не должна:
//   - авторизация проверяется в beforeLoad одного app-layout роута, а не в каждом роуте;
//   - роль и action проверяются по meta роута (allowedRoles / requiredAction);
//   - скрытие пункта меню НЕ заменяет guard — нужны оба;
//   - дерево роутов одно на все роли: разные URL есть только у входа.
//
// ГЛАВНОЕ СВОЙСТВО: guard НЕ ходит в сеть и не смотрит на срок жизни токена.
// WiFi на кухне нестабилен (инвариант 3), и guard, выкидывающий менеджера на /login
// при каждом обрыве, убивал бы Модуль Б на ровном месте. Войти офлайн всё равно
// невозможно (offline-first.md §4), поэтому протухание сессии обнаруживается первым
// 401 от сервера, а не локальным таймером.

import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';

import { getToken } from '@/shared/auth';
import { sessionKeys, type CurrentUser } from '@/shared/session';

/** Мета роута: кто имеет право его открыть. Читается guard'ом в beforeLoad. */
export type RouteAccessMeta = {
  /**
   * Роли, которым маршрут доступен. Пусто/не задано — доступен всем авторизованным.
   *
   * Тип нарочно `string`, а не union ролей: роли — доменная модель, её публичный тип
   * приезжает из modules/platform.
   */
  readonly allowedRoles?: readonly string[];
  /** Действие из permissions, без которого маршрут отдаёт ForbiddenPage. */
  readonly requiredAction?: string;
};

/**
 * Проверка сессии перед загрузкой защищённого поддерева роутов.
 *
 * Развилки «нет сессии, но есть привязка устройства» здесь БОЛЬШЕ НЕТ. Она вела
 * привязанный моноблок на киоск мимо формы входа — потому что у повара не было ни
 * email, ни пароля, и вести его на форму было бессмысленно.
 *
 * Логин появился у каждой роли (T-018): без сессии в приложении делать нечего никому,
 * включая кухню. Заодно ушёл дефект, который развилка несла с собой, — `hasDevicePairing`
 * возвращала константу `false`, а редирект целился в `/kiosk`, маршрута с таким путём
 * в дереве нет вовсе.
 */
export function requireAuth(currentPath: string): void {
  if (getToken()) {
    return;
  }

  // Исходный путь сохраняется, чтобы после входа человек попал туда, куда шёл:
  // ссылками на конкретные экраны внутри сети обмениваются.
  throw redirect({ to: '/login', search: { redirect: currentPath } });
}

/**
 * Проверка роли по мете роута. Возвращает false — рендерится ForbiddenPage.
 *
 * ForbiddenPage, а не молчаливый редирект: менеджер должен понимать, что экран
 * существует и закрыт, иначе это выглядит как поломка приложения.
 *
 * Профиль берётся из кэша QueryClient — того самого, что работает офлайн. Если
 * профиля ещё нет, отказывать нельзя: это состояние «грузим», а не «нет прав»,
 * и отказ здесь выкидывал бы пользователя на каждом холодном старте.
 */
export function requireAccess(queryClient: QueryClient, meta: RouteAccessMeta): boolean {
  const allowed = meta.allowedRoles;
  if (!allowed || allowed.length === 0) {
    return true;
  }

  const user = queryClient.getQueryData<CurrentUser>(sessionKeys.me);
  if (!user) {
    return true;
  }

  return allowed.includes(user.role);
}
